(ns app.front.view.property.gis-import.import-session
  (:require [app.domain.municipality.model :as municipality-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.table :as ui-table]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [app.front.view.property.gis-import.session-common :as gis-import-common]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

(rf/reg-event-fx ::start-db-import
  (fn [_ [_ session-id]]
    {:fx [[:dispatch [:comm/command-monitored
                      {:command    :property-import/start-property-import
                       :data       (util/mmap session-id)
                       :on-success [::entities/apply-changes]
                       :notify     {:success :data-import/import-started}}]]]}))

(rf/reg-event-fx ::fetch-imported-properties
  (fn [_ [_ session-id]]
    {:comm/command {:command    :property-import/get-imported-properties
                    :method     :get
                    :data       {:session-id session-id
                                 :from       0
                                 :size       100}
                    :on-success [::property-dashboard-subs/set-imported-properties session-id]}}))

(rf/reg-event-fx ::load-session-records
  (fn [_ [_ session-id]]
    {:fx [[:dispatch [::fetch-imported-properties session-id]]
          [:dispatch [::property-dashboard-subs/fetch-temp-records session-id]]
          [:dispatch [::property-dashboard-subs/fetch-duplicate-records session-id]]]}))

(rf/reg-event-fx ::update-session-records
  (fn [_ [_ session-id]]
    {:fx [[:dispatch [::fetch-imported-properties session-id]]
          [:dispatch [::property-dashboard-subs/fetch-temp-records session-id]]
          [:dispatch-debounce {:id      ::fetch-duplicate-records
                               :timeout 5000
                               :action  :dispatch
                               :event   [::property-dashboard-subs/fetch-duplicate-records session-id]}]]}))

(rf/reg-sub ::any-job-running?
  :<- [::gis-import-common/async-jobs]
  :<- [:comm/command-processing? :property-import/start-property-import]
  (fn [[jobs cmd-running?] _]
    (boolean (or cmd-running?
                 (some (du/in-state-fn :async-job.state/running) jobs)))))


(rf/reg-sub ::default-duplicate-group-count
  (fn [[_ session-id]]
    [(rf/subscribe [::property-dashboard-subs/import-duplicate-records session-id])
     (rf/subscribe [::gis-import-common/duplicate-resolutions])
     (rf/subscribe [::ff/enabled? :feature-flag.id/property-data-integrity-tooling])])
  (fn [[{:keys [groups]} resolutions enabled?] _]
    (if enabled?
      (->> groups
           (remove #(contains? resolutions (:property/id %)))
           count)
      0)))

(rf/reg-sub ::back-href
  (fn [_]
    (href/href :view/property-gis-import)))


(rf/reg-sub ::view-data
  :<- [::back-href]
  (fn [back-href]
    {:back-href back-href}))


(defn view [{{{:keys [session-id]} :path} :parameters :as match}]
  (when-let [session (<sub [::gis-import-common/session])]
    (let [tr              (<sub [::gis-import-common/property-tr])
          lang            (<sub [:language])
          format-date     (<sub [::date-util/date-time-fn])
          temp-records    (<sub [::property-dashboard-subs/import-temp-records session-id])
          duplicate-data  (<sub [::property-dashboard-subs/import-duplicate-records session-id])
          duplicates      (:groups duplicate-data)
          imported-props  (<sub [::property-dashboard-subs/imported-properties session-id])
          async-jobs      (<sub [::gis-import-common/async-jobs])
          job-running?    (<sub [::any-job-running?])
          export-ready?   (and (not job-running?)
                               (property-model/duplicate-records-exportable? async-jobs))
          default-groups  (<sub [::default-duplicate-group-count session-id])
          tooling?        (<sub [::ff/enabled? :feature-flag.id/property-data-integrity-tooling])
          start-disabled? (or job-running?
                              (empty? temp-records)
                              (and tooling? (not duplicate-data)))
          created         (:entity/created session)
          expiry-ts       (date-util/goog-add-days (date-util/js-date->goog-date-time created)
                                                   property-model/property-gis-import-session-max-age-days)]
      [:div.cloudpermit-page
       [ui/page-header (util/mmap match)
        [:h1 (str (tr [:property-gis-import/session-heading])
                  ": "
                  (-> session :property-import-session/municipality (municipality-model/municipality-name lang)))]]
       [:section.p-8
        [:div.mb-4
         [gis-import-common/jobs-table (util/mmap async-jobs tr)]]
        [:div.mb-4
         [ui/help
          (tr [:property-gis-import/import-guide]
              [(tr [:property/id])])]
         (when (and created (> (date-util/period (date-util/now) created :days) 1))
           [ui/warning
            (tr [:property-gis-import/expiry-warning]
                [(format-date expiry-ts)])])
         [:div.flex.w-full.justify-end.gap-4
          [ui/modal-confirmation-button
           {:id                  "remove-session-button"
            :tr                  tr
            :trigger-button-text (tr [:property-gis-import/remove-session])
            :trigger-button-opts {:kind :danger
                                  :size :field}
            :on-yes-click        #(>evt [::gis-import-common/remove-import-session
                                         session-id
                                         :property-gis-import/session-removed])}
           (tr [:property-gis-import/remove-session-confirm])]
          [ui/modal-confirmation-button
           {:id                  "start-db-import-button"
            :tr                  tr
            :trigger-button-text (tr [:property-gis-import/start-db-import])
            :trigger-button-opts {:kind :primary
                                  :size :field}
            :disabled?           start-disabled?
            :skip-confirm?       (zero? default-groups)
            :on-yes-click        #(>evt [::start-db-import session-id])}
           (if (= 1 default-groups)
             (tr [:property-gis-import/default-duplicates-confirm-single]
                 [(property-model/format-property-id-for-sentence tr)])
             (tr [:property-gis-import/default-duplicates-confirm-multi]
                 [default-groups (property-model/format-property-id-for-sentence tr)]))]
          (when job-running?
            [:div.flex.items-center (cc/inline-loading {:class-name "min-h-0"})])]]
        (when (and tooling?
                   (seq duplicates))
          (let [property-id     (property-model/format-property-id-for-sentence tr)
                duplicate-total (:total duplicate-data)]
            [:div.mb-8
             [:h3.mb-4 (tr [:property-gis-import/duplicate-records] [property-id])]
             [ui/warning
              (if (= 1 duplicate-total)
                (tr [:property-gis-import/duplicate-warning-single] [property-id])
                (tr [:property-gis-import/duplicate-warning-multi] [duplicate-total property-id]))]
             (when export-ready?
               [:div.mb-4.flex.flex-col.items-end
                [:span.bx--label.text-right (tr [:property-gis-import/export-duplicate-records])]
                (cc/button
                  {:icon-position :right
                   :icon          i/download
                   :size          :field
                   :href          (str "/api/command/property-import/download-duplicate-records?session-id=" session-id)}
                  (tr [:export]))])
             [gis-import-common/duplicate-records-table (assoc (util/mmap duplicates tr) :disabled? job-running?)]]))
        [gis-import-common/temp-records-table (util/mmap temp-records tr)]
        [:h3.mt-8.mb-4 (tr [:property-gis-import/imported-properties])]
        (when (seq imported-props)
          [:div.mb-4.text-xs (tr [:property-gis-import/first-n-displayed] ["100"])])
        [ui-table/modern-responsive-table
         {:tr                tr
          :items             imported-props
          :empty-placeholder (tr [:property-gis-import/no-records])
          :id-func           :id
          :link              {:description (tr [:open])
                              :make-href   #(href/href :view/property
                                                       {:db-id (:id %)})}
          :cols              [[:property/id
                               :property/id]
                              [:address/full-address
                               :property/primary-address]]}]]])))


(def route
  {:name        :view/property-gis-import-session
   :view-sub    [::view-data]
   :view        view
   :parameters  {:path {:session-id s/Int}}
   :controllers [{:parameters {:path [:session-id]}
                  :start      (fn [{{:keys [session-id]} :path}]
                                (>evt [::gis-import-common/init session-id ::load-session-records]))
                  :stop       (fn [{{:keys [session-id]} :path}]
                                (>evt [::property-dashboard-subs/remove-import-session-data session-id])
                                (>evt [::gis-import-common/clear]))}]})
